import type { Account, Budget, Contact, MasterItem, MasterType } from '../types';
import { DROPDOWN_GROUPS, normalizeDropdownValue } from '../types/dropdowns';

export type SettingsSection = 'hub' | 'categories' | 'paymentModes' | 'contacts' | 'budgets' | 'accounts' | 'planSettings' | 'notifications';

const sectionAliases: Record<string, SettingsSection> = {
  categories: 'categories',
  'payment-modes': 'paymentModes',
  paymentmodes: 'paymentModes',
  contacts: 'contacts',
  budgets: 'budgets',
  accounts: 'accounts',
  cards: 'accounts',
  notifications: 'notifications',
  'plan-settings': 'planSettings',
  plans: 'planSettings'
};

const categoryGroups: MasterType[] = [
  DROPDOWN_GROUPS.DAILY_CATEGORY,
  DROPDOWN_GROUPS.INCOME_CATEGORY,
  DROPDOWN_GROUPS.INVESTMENT_CATEGORY,
  DROPDOWN_GROUPS.PLAN_CATEGORY
];

const planGroups: MasterType[] = [DROPDOWN_GROUPS.PLAN_FREQUENCY, DROPDOWN_GROUPS.FLOW, DROPDOWN_GROUPS.CRDR_TYPE, DROPDOWN_GROUPS.TAX_TAG];

export function resolveSettingsSection(hash = ''): SettingsSection {
  const key = hash.replace(/^#\/?settings\/?/, '').replace(/^#/, '').trim().toLowerCase();
  return sectionAliases[key] || 'hub';
}

export function settingsSectionHash(section: SettingsSection) {
  if (section === 'hub') return '#settings';
  const slug = section === 'paymentModes' ? 'payment-modes' : section === 'planSettings' ? 'plan-settings' : section;
  return `#settings/${slug}`;
}

export function countActiveMasters(masters: MasterItem[], groups: MasterType[]) {
  const allowed = new Set(groups);
  return masters.filter((item) => item.active !== false && allowed.has((item.group || item.type) as MasterType)).length;
}

export function findDuplicateMasters(masters: MasterItem[]) {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  masters.forEach((item) => {
    const group = item.group || item.type;
    const key = `${group}:${normalizeDropdownValue(item.value)}`;
    if (seen.has(key)) duplicates.add(`${group} / ${item.value}`);
    seen.add(key);
  });
  return Array.from(duplicates).sort((a, b) => a.localeCompare(b));
}

export function buildSettingsSummary(input: {
  masters: MasterItem[];
  contacts: Contact[];
  budgets: Budget[];
  accounts: Account[];
  monthKey: string;
}) {
  const activeAccounts = input.accounts.filter((account) => account.status !== 'deleted' && account.status !== 'archived' && account.active !== false);
  const creditCards = activeAccounts.filter((account) => account.type === 'credit_card');
  const activeBudgets = input.budgets.filter((budget) => budget.status !== 'deleted' && budget.active !== false && budget.monthKey === input.monthKey);
  const activeContacts = input.contacts.filter((contact) => contact.active !== false);
  const duplicateMasters = findDuplicateMasters(input.masters);

  return {
    categories: countActiveMasters(input.masters, categoryGroups),
    paymentModes: countActiveMasters(input.masters, [DROPDOWN_GROUPS.PAYMENT_MODE]),
    planSettings: countActiveMasters(input.masters, planGroups),
    contacts: activeContacts.length,
    googleContacts: activeContacts.filter((contact) => contact.source === 'google').length,
    budgets: activeBudgets.length,
    accounts: activeAccounts.length,
    creditCards: creditCards.length,
    warnings: [
      activeAccounts.length === 0 ? 'No active account or card is set up yet.' : '',
      creditCards.some((card) => !Number(card.creditLimit || 0)) ? 'One or more credit cards have no credit limit.' : '',
      activeBudgets.length === 0 ? `No budgets saved for ${input.monthKey}.` : '',
      duplicateMasters.length ? `${duplicateMasters.length} duplicate dropdown value(s) need review.` : ''
    ].filter(Boolean)
  };
}
